import { useRouter } from 'next/router';
import { ReactNode, ReactElement, createContext } from 'react';

import Meta from '@components/Meta';
import { PageWrapperStyles } from '@common/PageWrapper';
import { usePrevious } from '@hooks/generalHooks';
import QuizProvider from '@components/context/QuizProvider';
import VitalityQuizProvider from '../context/VitalityQuizProvider';
import NHSSignupProvider from '../context/NHSSignupProvider';
import MseSignupProvider from '../context/MseSignupProvider';

// Context to hold the path of the previous page.
export const PreviousPageContext = createContext<string | undefined>(undefined);

/**
 * `usePreviousPagePath` is a custom hook that captures and returns the path of the last visited page.
 * It utilizes the `usePrevious` hook to keep track of the `router.asPath` value before it changes.
 *
 * @returns {string | undefined} The path of the previous page or `undefined` if it's the first page.
 */
export const usePreviousPagePath = (): string | undefined => {
  const router = useRouter();

  return usePrevious(router.asPath);
};

export const PageWrapperQuiz = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const router = useRouter();
  const previousPagePath = usePreviousPagePath();

  // We only index the index page e.g. `/get-plan` not the quiz pages e.g. `/get-plan/priorities`
  // This is to prevent search engines from indexing the quiz pages which we don't want to be indexed.
  const shouldNotBeIndexed = router.asPath !== '/get-plan';

  return (
    <PreviousPageContext.Provider value={previousPagePath}>
      <Meta
        title="Health Quiz | Second Nature"
        metaDescription="Take our health quiz to see how the Second Nature programme can help you"
        noFollow={shouldNotBeIndexed}
        customProperties={{ hideIntercom: true }}
      />
      <PageWrapperStyles>{children}</PageWrapperStyles>
    </PreviousPageContext.Provider>
  );
};

export const PageWrapperNHSQuiz = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const router = useRouter();
  const previousPagePath = usePreviousPagePath();

  // We only index the index page e.g. `/partnerships/signupFlowID` not the quiz pages e.g. `/partnerships/signupFlowID/priorities`
  // based on the url we can determine if we should not be indexed
  // e.g. if the url is `/partnerships/ndpp-thrive-tribe/nhs` then we should not be indexed
  const signupFlowId = router.asPath.split('/')[2];
  const shouldNotBeIndexed = router.asPath !== `/partnerships/${signupFlowId}`;

  return (
    <PreviousPageContext.Provider value={previousPagePath}>
      <Meta
        title="Health Quiz | Second Nature"
        metaDescription="Take our health quiz to see how the Second Nature programme can help you"
        noFollow={shouldNotBeIndexed}
        customProperties={{ hideIntercom: true }}
      />
      <PageWrapperStyles>{children}</PageWrapperStyles>
    </PreviousPageContext.Provider>
  );
};

export const PageWrapperMseQuiz = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const router = useRouter();
  const previousPagePath = usePreviousPagePath();

  // We only index the index page e.g. `/partnerships/signupFlowID` not the quiz pages e.g. `/partnerships/signupFlowID/priorities`
  // based on the url we can determine if we should not be indexed
  // e.g. if the url is `/partnerships/ndpp-thrive-tribe/nhs` then we should not be indexed
  const signupFlowId = router.asPath.split('/')[2];
  const shouldNotBeIndexed = router.asPath !== `/partnerships/${signupFlowId}`;

  return (
    <PreviousPageContext.Provider value={previousPagePath}>
      <Meta
        title="Health Quiz | Second Nature"
        metaDescription="Take our health quiz to see how the Second Nature programme can help you"
        noFollow={shouldNotBeIndexed}
        customProperties={{ hideIntercom: true }}
      />
      <PageWrapperStyles>{children}</PageWrapperStyles>
    </PreviousPageContext.Provider>
  );
};

export const VitalityPageWrapperQuiz = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const previousPagePath = usePreviousPagePath();
  return (
    <PreviousPageContext.Provider value={previousPagePath}>
      <Meta
        title="Vitality Health Quiz | Second Nature"
        metaDescription="Take our health quiz to see how the Second Nature programme can help you"
        noFollow
        customProperties={{ hideIntercom: true }}
      />
      <PageWrapperStyles>{children}</PageWrapperStyles>
    </PreviousPageContext.Provider>
  );
};

export const NavigationButtonWrapperQuiz = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <div
    className={`fixed inset-x-0 bottom-0 z-10 m-auto flex w-full max-w-md flex-1 border-t border-dawn-blue-400 bg-dawn-blue-100 px-4 pb-4 pt-2 shadow-md md:relative md:border-0 md:bg-transparent md:px-0 md:shadow-none`}
  >
    <div className="w-full">{children}</div>
  </div>
);

export const NavigationButtonWrapperQuizInterim = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <div
    className={`fixed inset-x-0 bottom-0 z-10 m-auto flex w-full max-w-lg flex-1 bg-dawn-blue-100 px-4 pb-4 pt-2 shadow-md md:relative md:border-0 md:bg-transparent md:px-0 md:shadow-none`}
  >
    <div className="w-full">{children}</div>
  </div>
);

/**
 * `QuizLayout` is a higher-order component that wraps the quiz page
 * with the `QuizProvider` context provider and the `PageWrapperQuiz` component.
 *
 * This component is typically used in the `getLayout` function of a page. For example:
 *
 * ```typescript
 * // pages/index.tsx
 * IndexPage.getLayout = (page: ReactElement): ReactElement => {
 *   return QuizLayout(page);
 * };
 * ```
 *
 * In the `_app.tsx` file, the `getLayout` function is called to wrap the current page with the layout provided by the page:
 *
 * ```typescript
 * // next/pages/_app.tsx
 * const getLayout = Component.getLayout ?? (page => page);
 * {getLayout(<Component {...pageProps} />, pathname)}
 * ```
 *
 * This setup allows each page to define its own layout logic, which can include wrapping the page in context providers like `PreviousPageContext` to pass down the previous page path state.
 *
 * @param {ReactElement} page - The quiz page element to be wrapped.
 * @returns {ReactElement} The wrapped quiz page with context providers.
 */
export const QuizLayout = (page: ReactElement): ReactElement => (
  <QuizProvider>
    <PageWrapperQuiz>{page}</PageWrapperQuiz>
  </QuizProvider>
);

export const VitalityQuizLayout = (page: ReactElement): ReactElement => (
  <VitalityQuizProvider>
    <VitalityPageWrapperQuiz>{page}</VitalityPageWrapperQuiz>
  </VitalityQuizProvider>
);

export const PartnershipQuizLayout = (page: ReactElement): ReactElement => (
  <NHSSignupProvider>
    <PageWrapperNHSQuiz>{page}</PageWrapperNHSQuiz>
  </NHSSignupProvider>
);

export const MseQuizLayout = (page: ReactElement): ReactElement => (
  <MseSignupProvider>
    <PageWrapperMseQuiz>{page}</PageWrapperMseQuiz>
  </MseSignupProvider>
);
